import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { baseurl } from '../utility/helper';
import { ctxParams } from '../utility/instituteHelpers';
import '../assets/css/Institute.css';

const emptyForm = { name: '', location: '', approved_fee: '', status: 'Active' };

const InstituteForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instituteId } = location.state || {};
  const isEdit = !!instituteId;

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(false); // existing institutes open read-only first
  const viewOnly = isEdit && !editing;

  useEffect(() => {
    if (!isEdit) { setForm(emptyForm); return; }
    axios.get(`${baseurl}/admin/institutes/${instituteId}`)
      .then((res) => {
        const i = res.data;
        setForm({ name: i.name || '', location: i.location || '', approved_fee: i.approved_fee ?? '', status: i.status || 'Active' });
      })
      .catch((err) => {
        console.error('Error loading institute:', err);
        Swal.fire('Error', 'Could not load institute', 'error');
      });
  }, [isEdit, instituteId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = 'Institute name is required';
    if (!form.location.trim()) err.location = 'Location is required';
    if (form.approved_fee === '' || Number(form.approved_fee) < 0) err.approved_fee = 'Enter a valid fee';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = { ...form, approved_fee: Number(form.approved_fee), ...ctxParams() };
    try {
      if (isEdit) {
        await axios.put(`${baseurl}/admin/institutes/${instituteId}`, payload);
        Swal.fire('Updated!', 'Institute updated successfully.', 'success');
      } else {
        await axios.post(`${baseurl}/admin/institutes`, payload);
        Swal.fire('Success!', 'Institute created successfully.', 'success');
      }
      navigate('/admin/InstituteList');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save institute', 'error');
    }
  };

  const handleDelete = async () => {
    const c = await Swal.fire({
      title: 'Delete this institute?',
      text: 'An institute that has batches cannot be deleted.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!',
    });
    if (!c.isConfirmed) return;
    try {
      await axios.delete(`${baseurl}/admin/institutes/${instituteId}`, { params: ctxParams() });
      await Swal.fire('Deleted!', 'Institute removed.', 'success');
      navigate('/admin/InstituteList');
    } catch (err) {
      const blocked = err.response?.status === 400;
      Swal.fire(blocked ? 'Cannot Delete' : 'Error', err.response?.data?.message || 'Failed to delete institute', blocked ? 'warning' : 'error');
    }
  };

  return (
    <div className="content container-fluid inst-wrap">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h3 className="mb-0"><b>{!isEdit ? 'Add Institute' : viewOnly ? 'Institute Details' : 'Edit Institute'}</b></h3>
        <div>
          {viewOnly && (
            <>
              <button type="button" className="btn btn-warning" onClick={() => setEditing(true)}>
                <i className="fas fa-edit" /> Edit
              </button>
            </>
          )}
          {isEdit && (
            <>
              &nbsp;&nbsp;
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                <i className="fas fa-trash" /> Delete Institute
              </button>
            </>
          )}
          </div> 
      </div>

      <div className="inst-card">
        <form onSubmit={handleSubmit} className="row g-3">
          <fieldset disabled={viewOnly} className="row g-3 m-0 p-0 border-0">
          <div className="col-md-6">
            <label className="form-label">Institute Name</label>
            <input className="form-control" name="name" value={form.name} onChange={handleChange} />
            {errors.name && <small className="text-danger">{errors.name}</small>}
          </div>
          <div className="col-md-6">
            <label className="form-label">Location</label>
            <input className="form-control" name="location" value={form.location} onChange={handleChange} />
            {errors.location && <small className="text-danger">{errors.location}</small>}
          </div>
          <div className="col-md-6">
            <label className="form-label">Approved Fee (₹)</label>
            <input type="number" min="0" step="0.01" className="form-control"
              name="approved_fee" value={form.approved_fee} onChange={handleChange} />
            {errors.approved_fee && <small className="text-danger">{errors.approved_fee}</small>}
          </div>
          <div className="col-md-6">
            <label className="form-label">Status</label>
            <select className="form-select" name="status" value={form.status} onChange={handleChange}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          </fieldset>
          <div className="col-12 text-center">
            <Link to="/admin/InstituteList">
              <button type="button" className="btn btn-secondary">{viewOnly ? 'Back' : 'Cancel'}</button>
            </Link>
            {!viewOnly && (
              <>
                &nbsp;&nbsp;
                <button type="submit" className="btn btn-primary" style={{ borderRadius: 8 }}>
                  {isEdit ? 'Update' : 'Create'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstituteForm;
